import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import CircuitDayPricing, {
  CircuitStepsPricingProps,
  CircuitStepsPricingView,
} from '../circuits/CircuitDayPricing';
import HorizontalLine from '../ui/HorizontalLine';
import { formatPrice, roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { addDays } from 'date-fns';
import { CircuitDayPrices } from '@/utils/circuits/prices/computeBenefitPrices';
import { useCircuitStepPricing } from '@/hooks/useCircuitPrices';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import StyledButton from '../styled/StyledButton';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import {
  useAccommodationBenefit,
  useTransportBenefit,
} from '@/hooks/useReservationSummaryBenefit';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { EstimateResponse } from '@/types/estimates/EstimateResponse';
import { useConvertPrice } from '@/hooks/useCurrencyExchange';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useTranslation } from 'react-i18next';
import { SupportedLocale } from '@/types/supported-locale.type';

interface ReservationSummaryProps {
  reservationDaysObject: CircuitDayPrices[];
  reservationDaysTrackingMultiDayBenefits: CircuitDayFormData[];
  selectedArrivalDate: Date;
  selectedOptions: EstimateFormData['selectedEstimateOptions'];
  onAccept: () => void;
  onRefuse: () => void;
  status: EstimateStatus;
  adults: EstimateResponse['adults'];
  children: EstimateResponse['children'];
  currency: string;
  creationDate: Date;
  language?: SupportedLocale;
}

const ReservationSummaryPage = ({
  reservationDaysObject,
  reservationDaysTrackingMultiDayBenefits,
  selectedArrivalDate,
  selectedOptions,
  onAccept,
  onRefuse,
  status,
  adults,
  children,
  currency,
  creationDate,
  language,
}: ReservationSummaryProps) => {
  const { t } = useTranslation();
  const nbDaysArray = useMemo(
    () =>
      Array.from(
        Array(Number(reservationDaysTrackingMultiDayBenefits.length)).keys(),
      ),
    [reservationDaysTrackingMultiDayBenefits.length],
  );

  const totalDefaultValue = useMemo(
    () =>
      reservationDaysTrackingMultiDayBenefits.map((day) =>
        day.steps.map(() => 0),
      ),
    [reservationDaysTrackingMultiDayBenefits],
  );

  const [totals, setTotals] = useState<number[][]>(totalDefaultValue);

  const getDayTotal = useCallback(
    (dayIndex: number) => totals[dayIndex].reduce((a, b) => a + b, 0),
    [totals],
  );

  const totalPriceWithOptions = useMemo(
    () => nbDaysArray.map((day) => getDayTotal(day)).reduce((a, b) => a + b, 0),
    [getDayTotal, nbDaysArray],
  );

  return (
    <div className="flex flex-col items-center gap-4 p-6">
      <div className="flex flex-col md:flex-row gap-6 w-full">
        <div className="md:sticky md:top-20 z-10 md:w-1/4 flex flex-col h-fit">
          {nbDaysArray.map((_, index) => (
            <div
              key={`circuit-day-${index}`}
              className="flex justify-between bg-brand-50 pr-4 border rounded-md"
              onClick={() => {
                window.location.replace(`#day-${index}`);
              }}
            >
              <div className="w-full h-full pl-4 py-3 hover:cursor-pointer">
                <h2 className="text-base font-medium text-black w-full h-full">
                  {t('reservations.summary.day', { index: index + 1 })}
                </h2>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col w-full gap-4">
          {nbDaysArray.map((displayedIndex) => (
            <Fragment key={`tarif-index-${displayedIndex}`}>
              <CircuitDayPricing
                index={displayedIndex}
                day={
                  reservationDaysTrackingMultiDayBenefits[displayedIndex] ?? {
                    steps: [],
                  }
                }
                totalPrice={totals[displayedIndex].reduce((a, b) => a + b, 0)}
                circuitDayPrices={reservationDaysObject[displayedIndex]}
                date={addDays(selectedArrivalDate, displayedIndex)}
                PricingComponent={ReservationCircuitStepsPricing}
                selectedOptions={selectedOptions}
                setTotals={setTotals}
                adults={adults}
                children={children}
                currency={currency}
                creationDate={creationDate}
                language={language}
              />
              <HorizontalLine />
            </Fragment>
          ))}
          <div className="flex w-full justify-between p-4 rounded-lg bg-brand-50 border border-brand-600">
            <h3 className="text-base font-semibold">
              {t('reservations.summary.totalCircuit')}
            </h3>
            <p className="text-base font-semibold">
              {formatPrice(totalPriceWithOptions)} {formatCurrency(currency)}
            </p>
          </div>
        </div>
      </div>
      {status === EstimateStatus.ESTIMATE && (
        <div className="md:sticky md:bottom-0 md:z-50 flex justify-end-safe gap-4 w-full bg-white p-4">
          <StyledButton
            title={t('reservations.summary.accept')}
            className="bg-success-600 px-3 py-2 border-success-700 hover:bg-success-700"
            onClick={onAccept}
          />
          <StyledButton
            title={t('reservations.summary.refuse')}
            className="bg-error-600 px-3 py-2 border-error-700 hover:bg-error-700"
            onClick={onRefuse}
          />
        </div>
      )}
    </div>
  );
};

export default ReservationSummaryPage;

const ReservationCircuitStepsPricing = ({
  index,
  stepIndex,
  step,
  date,
  circuitStepPricing,
  selectedOptions,
  setTotals,
  adults = [],
  children = [],
  currency,
  creationDate,
  language,
}: CircuitStepsPricingProps) => {
  const {
    standardTransport,
    standardActivity,
    standardMeal,
    standardAccommodation,
    atLeastOneStandard,
  } = useCircuitStepPricing({
    circuitStepPricing,
    date,
    step,
    language,
  });

  const { convertPrice } = useConvertPrice(creationDate);

  const standardAccommodationWithAllCombinations = useAccommodationBenefit({
    standardAccommodation,
    selectedOptions: selectedOptions as Record<string, SelectedEstimateOption>,
    step,
    date,
    adults,
    children,
  });

  const standardTransportWithCarRentalOptions = useTransportBenefit({
    standardTransport,
    selectedOptions: selectedOptions as Record<string, SelectedEstimateOption>,
    step,
    isFirstDay: index === step.transport.standardBenefit?.indexFirstDay,
  });

  useEffect(() => {
    if (setTotals !== undefined) {
      const getPrice = (pricingData: typeof standardActivity) =>
        roundUpPrice(
          roundUpPrice(pricingData?.price?.minimumPrice ?? 0) *
            (1 + roundUpPrice(pricingData?.marginRate ?? 0) / 100),
        );

      const activityPrice = roundUpPrice(
        convertPrice(
          getPrice(standardActivity),
          standardActivity?.currency ?? 'EUR',
          currency ?? 'EUR',
        ),
      );
      const mealPrice = roundUpPrice(
        convertPrice(
          getPrice(standardMeal),
          standardMeal?.currency ?? 'EUR',
          currency ?? 'EUR',
        ),
      );
      const accommodationPrice = roundUpPrice(
        convertPrice(
          getPrice(standardAccommodationWithAllCombinations),
          standardAccommodationWithAllCombinations?.currency ?? 'EUR',
          currency ?? 'EUR',
        ),
      );
      const transportPrice = roundUpPrice(
        convertPrice(
          getPrice(standardTransportWithCarRentalOptions),
          standardTransportWithCarRentalOptions?.currency ?? 'EUR',
          currency ?? 'EUR',
        ),
      );
      const stepPrice =
        activityPrice + mealPrice + accommodationPrice + transportPrice;
      setTotals((prevTotals) =>
        prevTotals.with(index, prevTotals[index].with(stepIndex, stepPrice)),
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CircuitStepsPricingView
      index={index}
      stepIndex={stepIndex}
      atLeastOneStandard={atLeastOneStandard}
      optionalLength={0}
      standardTransport={standardTransportWithCarRentalOptions}
      standardActivity={standardActivity}
      standardMeal={standardMeal}
      standardAccommodation={standardAccommodationWithAllCombinations}
      optionalTransportBenefits={[]}
      optionalActivityBenefits={[]}
      optionalMealsBenefits={[]}
      optionalAccommodationsBenefits={[]}
      currency={currency}
    />
  );
};
